import { useState, useRef, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { MessageSquare, X, Send, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

export default function AIChatPanel() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const history = messages.map(m => ({ role: m.role, content: m.content }));
      const { data } = await api.post('/ai/chat', { message: text, conversation_history: history });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, model: data.model }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.', error: true }]);
    }
    setLoading(false);
  }, [input, loading, messages]);

  const quickAction = (text) => {
    setInput(text);
    setOpen(true);
  };

  if (!open) {
    return (
      <button data-testid="ai-chat-trigger" onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105"
        style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
        <Sparkles size={20} />
      </button>
    );
  }

  return (
    <div data-testid="ai-chat-panel" className="fixed bottom-0 right-0 z-50 w-full sm:w-[400px] h-[calc(100vh-60px)] sm:h-[600px] sm:bottom-6 sm:right-6 sm:rounded-lg shadow-2xl border flex flex-col overflow-hidden"
      style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.1)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(26,26,26,0.06)', backgroundColor: '#fff' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E85D3A' }}>
            <Sparkles size={14} color="#fff" />
          </div>
          <div>
            <p className="font-sans text-sm font-medium" style={{ color: '#1A1A1A' }}>CloudStore AI</p>
            <p className="font-sans text-[10px]" style={{ color: 'rgba(26,26,26,0.4)' }}>Powered by OpenRouter</p>
          </div>
        </div>
        <button data-testid="ai-chat-close" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[rgba(26,26,26,0.05)]">
          <X size={16} style={{ color: 'rgba(26,26,26,0.4)' }} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles size={28} className="mx-auto mb-3" style={{ color: 'rgba(26,26,26,0.15)' }} />
            <p className="font-serif text-base mb-1" style={{ color: '#1A1A1A' }}>How can I help?</p>
            <p className="font-sans text-xs mb-5" style={{ color: 'rgba(26,26,26,0.4)' }}>Ask me about SEO, product copy, branding, or marketing</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['Optimize my product titles', 'Suggest a brand tagline', 'Write a flash sale email'].map(q => (
                <button key={q} onClick={() => quickAction(q)}
                  className="px-3 py-1.5 rounded-full font-sans text-[11px] border transition-colors hover:bg-[rgba(26,26,26,0.03)]"
                  style={{ borderColor: 'rgba(26,26,26,0.1)', color: 'rgba(26,26,26,0.6)' }}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 ${m.role === 'user' ? '' : ''}`}
              style={{
                backgroundColor: m.role === 'user' ? '#E85D3A' : '#fff',
                color: m.role === 'user' ? '#fff' : '#1A1A1A',
                border: m.role === 'user' ? 'none' : '1px solid rgba(26,26,26,0.08)',
              }}>
              {m.role === 'assistant' ? (
                <div className="font-sans text-[13px] leading-relaxed prose prose-sm max-w-none [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h2]:font-semibold [&_h3]:font-semibold [&_p]:my-1.5 [&_ul]:my-1 [&_li]:my-0.5 [&_code]:text-xs [&_code]:bg-[rgba(26,26,26,0.05)] [&_code]:px-1 [&_code]:rounded"
                  dangerouslySetInnerHTML={{ __html: m.content.replace(/\n/g, '<br>').replace(/#{3}\s(.*?)(?=<br>|$)/g, '<h3>$1</h3>').replace(/#{2}\s(.*?)(?=<br>|$)/g, '<h2>$1</h2>').replace(/#{1}\s(.*?)(?=<br>|$)/g, '<h2>$1</h2>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/- (.*?)(?=<br>|$)/g, '&bull; $1') }} />
              ) : (
                <p className="font-sans text-[13px] leading-relaxed">{m.content}</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg border" style={{ borderColor: 'rgba(26,26,26,0.08)', backgroundColor: '#fff' }}>
              <Loader2 size={14} className="animate-spin" style={{ color: '#C4956A' }} />
              <span className="font-sans text-xs" style={{ color: 'rgba(26,26,26,0.4)' }}>Thinking...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(26,26,26,0.06)', backgroundColor: '#fff' }}>
        <div className="flex gap-2">
          <Textarea data-testid="ai-chat-input" value={input} onChange={e => setInput(e.target.value)}
            placeholder="Ask anything..." className="flex-1 font-sans text-sm rounded-none min-h-[40px] max-h-[100px] resize-none"
            style={{ borderColor: 'rgba(26,26,26,0.12)' }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} />
          <Button data-testid="ai-chat-send" onClick={sendMessage} disabled={!input.trim() || loading}
            className="self-end rounded-none h-10 w-10 p-0" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
